import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import { Alert } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { BackButton } from '../../components/atoms/index.js';
import { RichTooltip } from '../../components/atoms/RichTooltip.jsx';
import { HeaderOption } from '../../components/molecules/MetricAutocomplete.jsx';
import { MissingConfigDialog } from '../../components/molecules/MissingConfigDialog.jsx';
import { NavbarSlot } from '../../components/molecules/NavbarSlot.jsx';
import { HeadersMappingTable } from '../../components/organisms/HeadersMappingTable.jsx';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import {
  useDataFileMetrics,
  useHeadersMapping,
  useIsDataFileContextFilled,
  useSetGranularityLevel,
} from '../../queries/index.js';

export const HeadersMappingStep = () => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    dataFileId,
    dataFilePreview,
    goToNextStep,
    goToPrevStep,
    isSubmitting,
  } = useDataFileContext();

  const isContextFilled = useIsDataFileContextFilled(dataFileId);

  const {
    mutateAsync: setGranularityLevel,
    isPending: isSetGranularityLevelPending,
  } = useSetGranularityLevel(dataFileId);

  const [isMissingConfigModal, setIsMissingConfigModal] = useState(false);

  const {
    data: { items: tableHeaders, additional },
    isPending: isHeadersMappingLoading,
  } = useHeadersMapping(dataFileId, {
    enabled: !isSetGranularityLevelPending,
    throwOnError: true,
  });

  const { canBeMapped = false, maxCount = 0 } = additional || {};
  const {
    data: { systemMetrics, customMetrics },
    isLoading: isMetricsLoading,
  } = useDataFileMetrics(
    {
      dataFileId,
      granularityLevel: dataFilePreview.granularityLevel ?? null,
    },
    { enabled: !isSetGranularityLevelPending, throwOnError: true },
  );

  const isLoading = isHeadersMappingLoading || isMetricsLoading;

  const isUploading = dataFilePreview.status === 'uploading';

  const customMetricsHeaderOptions: HeaderOption[] = customMetrics.map(
    (customMetric) => ({
      ...customMetric,
      headerType: 'custom_metric',
    }),
  );

  const headerOptions = [...systemMetrics, ...customMetricsHeaderOptions].map(
    (headerOption) => {
      const selected = tableHeaders.some(
        ({ mappedHeader }) => mappedHeader === headerOption.name,
      );

      return {
        ...headerOption,
        selected,
        disabled:
          selected ||
          Boolean(headerOption.isDisabled) ||
          (headerOption.headerType !== 'dimension' && !canBeMapped),
      };
    },
  );

  const isRequiredOptionsSelected = headerOptions.every(
    (obj) => !obj.required || obj.selected,
  );

  const selectedOptions = headerOptions.filter((header) => header.selected);
  const isMetricSelected = selectedOptions.some(
    ({ headerType }) =>
      headerType === 'metric' || headerType === 'custom_metric',
  );
  const hasDeactivatedMetrics = selectedOptions.some(
    ({ isDisabled }) => isDisabled,
  );

  const handleSubmit = () => {
    const isAllHeadersMapped = tableHeaders.every(
      (header) => header.mappedHeader,
    );

    if (!isAllHeadersMapped) {
      setIsMissingConfigModal(true);
      return;
    }
    goToNextStep();
  };

  const handleCloseMissingConfigModal = () => {
    setIsMissingConfigModal(false);
  };

  const handleMissingConfigModalSubmit = () => {
    setIsMissingConfigModal(false);
    goToNextStep();
  };

  const getNextButtonTooltip = () => {
    if (isLoading) {
      return null;
    }
    if (isUploading) {
      return t(
        'You will be able to proceed to the next step as soon as the file is fully uploaded.',
      );
    }
    if (!isContextFilled) {
      return t(
        `Please fulfill the obligatory ingestion context before processing.`,
      );
    }
    if (hasDeactivatedMetrics) {
      return '';
    }
    if (!isRequiredOptionsSelected || !isMetricSelected) {
      return t('Map obligatory dimensions and at least one metric to proceed.');
    }

    return null;
  };

  const nextButtonTooltip = getNextButtonTooltip();
  const nextButtonDisabled =
    isLoading || nextButtonTooltip !== null || isSubmitting;

  return (
    <>
      <NavbarSlot>
        <BackButton onClick={goToPrevStep} disabled={isSubmitting} />
        <RichTooltip asChild={true} description={nextButtonTooltip} side="left">
          <div>
            <Button
              onClick={handleSubmit}
              disabled={nextButtonDisabled}
              data-dd-action-name="Next"
            >
              {t('Next')}
            </Button>
          </div>
        </RichTooltip>
      </NavbarSlot>
      {hasDeactivatedMetrics && (
        <Alert
          severity="error"
          variant="inline"
          title={t('Some metrics have been deactivated.')}
          description={t(
            'To proceed with the import, remove them from the Data mapping step.',
          )}
          removable={false}
          className="mb-6"
        />
      )}
      <h4 className={cn(typographyVariants({ variant: 'h4' }), 'mb-2')}>
        {t('Data mapping')}
      </h4>
      <p
        className={cn(
          typographyVariants({ variant: 'caption1', color: 'secondary' }),
          'mb-6',
        )}
      >
        {t(
          'Select an attribution level of your performance data and map your file header to the corresponding ARM items, include at least one custom metric.',
        )}
      </p>
      <HeadersMappingTable
        dataFileId={dataFileId}
        headers={tableHeaders}
        headerOptions={headerOptions}
        maxMetricsCount={maxCount}
        isMetricsLimitReached={!canBeMapped}
        isLoading={isLoading}
        setGranularityLevel={setGranularityLevel}
        isSetGranularityLevelPending={isSetGranularityLevelPending}
      />

      {isMissingConfigModal && (
        <MissingConfigDialog
          onSubmit={handleMissingConfigModalSubmit}
          onClose={handleCloseMissingConfigModal}
        />
      )}
    </>
  );
};
